import { memo, useCallback } from 'react';

import { MessageCirclePlusIcon, XIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';

import { useAnnotationsToggle } from '~/components/AnnotationsV2/hooks/useAnnotationsToggle';

const COMMENT_MODE_BUTTON_LABEL = 'Comment';
const COMMENT_MODE_BUTTON_EXIT_LABEL = 'Exit comment mode';

export type CommentModeButtonProps = {
  canAnnotateClip: boolean;
  onEnterCommentMode?: () => void;
};

export const CommentModeButton = memo(({ canAnnotateClip, onEnterCommentMode }: CommentModeButtonProps) => {
  const { showAnnotations, hideAnnotations, isAnnotationsEnabled } = useAnnotationsToggle();

  const handleClick = useCallback(() => {
    if (isAnnotationsEnabled) {
      hideAnnotations();
      return;
    }

    showAnnotations();
    onEnterCommentMode?.();
  }, [hideAnnotations, isAnnotationsEnabled, onEnterCommentMode, showAnnotations]);

  // Comment mode applies to any annotatable clip (image or video), so this self-gates on
  // annotation permission rather than clip type.
  if (!canAnnotateClip) {
    return null;
  }

  const label = isAnnotationsEnabled ? COMMENT_MODE_BUTTON_EXIT_LABEL : COMMENT_MODE_BUTTON_LABEL;

  return (
    <Tooltip label={label}>
      <IconButton
        adsIcon
        appearance="ghost"
        color="gray"
        data-testid="ASSET_MODAL_COMMENT_MODE_BUTTON"
        icon={isAnnotationsEnabled ? XIcon : MessageCirclePlusIcon}
        label={label}
        onClick={handleClick}
        size="small"
      />
    </Tooltip>
  );
});

CommentModeButton.displayName = 'CommentModeButton';
